import { Component, Input, forwardRef, inject } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { styleProperties } from '../../table/model/common-models/style.model';
import { EoBaseFormControlComponent } from '../eo-base-form-control/eo-base-form-control.component';
import { EoPicklistConfig } from './eo-pick-list-config.model';
import { EoPickListService } from './eo-pick-list.service';

@Component({
  selector: 'eo-picklist',
  templateUrl: './eo-pick-list.component.html',
  styleUrl: './eo-pick-list.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EoPickListComponent),
      multi: true,
    },
  ],
})
export class EoPickListComponent<T> extends EoBaseFormControlComponent {
  protected override _config?: EoPicklistConfig<T>;
  override get config(): EoPicklistConfig<T> | undefined {
    return this._config;
  }
  override set config(val: EoPicklistConfig<T> | undefined) {
    this._config = val;
    if (this.config) this.formControl = this.config.control;
    if (this.config?.sourceData) {
      this.onFetchSourceSuccess(this.config.sourceData);
    }
  }

  protected override getLabel(): string {
    return this.label ?? this.config?.label ?? '';
  }

  private _source?: T[];
  @Input() set source(val: T[]) {
    this._source = val;
  }
  get source(): T[] {
    return this._source ?? this.config?.sourceData ?? [];
  }

  private _target?: T[];
  @Input() set target(val: T[]) {
    this._target = val;
  }
  get target(): T[] {
    return this._target ?? this.config?.target ?? [];
  }

  private _sourceHeader?: string;
  @Input() set sourceHeader(val: string) {
    this._sourceHeader = val;
  }
  get sourceHeader(): string {
    return this._sourceHeader ?? this.config?.sourceHeader ?? 'Όλα τα στοιχεία';
  }

  private _targetHeader?: string;
  @Input() set targetHeader(val: string) {
    this._targetHeader = val;
  }
  get targetHeader(): string {
    return (
      this._targetHeader ?? this.config?.targetHeader ?? 'Επιλεγμένα στοιχεία'
    );
  }

  private _dragdrop?: boolean;
  @Input() set dragdrop(val: boolean) {
    this._dragdrop = val;
  }
  get dragdrop(): boolean {
    return this._dragdrop ?? this.config?.dragdrop ?? true;
  }

  private _responsive?: boolean;
  @Input() set responsive(val: boolean) {
    this._responsive = val;
  }
  get responsive(): boolean {
    return this._responsive ?? this.config?.responsive ?? true;
  }

  private _sourceStyle?: styleProperties;
  @Input() set sourceStyle(val: styleProperties) {
    this._sourceStyle = val;
  }
  get sourceStyle(): styleProperties {
    return this._sourceStyle ?? this.config?.sourceStyle ?? { height: '10rem' };
  }

  private _targetStyle?: styleProperties;
  @Input() set targetStyle(val: styleProperties) {
    this._targetStyle = val;
  }
  get targetStyle(): styleProperties {
    return this._targetStyle ?? this.config?.targetStyle ?? { height: '10rem' };
  }

  private _filterBy?: string;
  @Input() set filterBy(val: string) {
    this._filterBy = val;
  }
  get filterBy(): string {
    return this._filterBy ?? this.config?.filterBy ?? 'name';
  }

  private _optionValue?: string;
  @Input() set optionValue(val: string) {
    this._optionValue = val;
  }
  get optionValue(): string | undefined {
    return this._optionValue ?? this.config?.optionValue;
  }

  private _optionLabel?: string;
  @Input() set optionLabel(val: string) {
    this._optionLabel = val;
  }
  get optionLabel(): string {
    return this._optionLabel ?? this.config?.optionLabel ?? 'label';
  }

  private _sourceFilterPlaceholder?: string;
  @Input() set sourceFilterPlaceholder(val: string | undefined) {
    this._sourceFilterPlaceholder = val;
  }
  get sourceFilterPlaceholder(): string | undefined {
    return (
      this._sourceFilterPlaceholder ?? this.config?.sourceFilterPlaceholder
    );
  }

  private _targetFilterPlaceholder?: string;
  @Input() set targetFilterPlaceholder(val: string | undefined) {
    this._targetFilterPlaceholder = val;
  }
  get targetFilterPlaceholder(): string | undefined {
    return (
      this._targetFilterPlaceholder ?? this.config?.targetFilterPlaceholder
    );
  }

  _apiUrl?: string;
  @Input() set apiUrl(val: string | undefined) {
    this._apiUrl = val;
  }
  get apiUrl(): string | undefined {
    return this._apiUrl ?? this.config?.apiUrl;
  }

  picklistService = inject(EoPickListService);

  override ngOnInit(): void {
    if (this.apiUrl) this.fetchSource();
  }

  onPicklistChange(): void {
    let formValue;
    if (this.optionValue)
      formValue = this.target.map((item) => (item as any)[this.optionValue!]);
    else formValue = this.target;

    this.formControl.setValue(formValue);
    this.onChange.emit(this.target);
  }

  fetchSource(): void {
    this.picklistService
      .fetchSource(this.apiUrl ?? '')
      .subscribe((data) => this.onFetchSourceSuccess(data));
  }

  onFetchSourceSuccess(data: T[]): void {
    this._source = data.filter((s) => {
      return !this.formControl.value?.some(
        (item: any) =>
          this.optionValue
            ? (item === (s as any)[this.optionValue])
            : JSON.stringify(item) === JSON.stringify(s) // Compare objects by value
      );
    });

    this._target = data.filter((s) => {
      return this.formControl.value?.some(
        (item: any) =>
          this.optionValue
            ? (item === (s as any)[this.optionValue])
            : JSON.stringify(item) === JSON.stringify(s) // Compare objects by value
      );
    });
  }
}
